import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson } from '../utils/authClient.js';
import MermaidDiagram from '../components/MermaidDiagram.jsx';
import { AdminSection, fill } from '../components/AdminNav.jsx';
import { FactGrid, formatBerlin } from './VeridionShared.jsx';

// The sovereign LLM architectures (#llm, IDD D-HP-ADMIN-062/064/065/071/075) in two sections from one payload
// (`GET /api/admin/veridion-core/quantum-architecture`): `paper` renders each strand's generated `paper`
// document in the single-column look of a classic machine-learning paper — an English-only scientific
// document with end-notes, a generated nomenclature and continuously numbered table captions
// (D-VCORE-351), exportable as a server-typeset PDF per strand; `monitoring` renders the selected strand's
// operational state (architecture status, interface and artifact state, GPU/VRAM facts, promotion gate,
// candidates, evaluations, changelog, events) plus the per-strand implementation status. A strand switcher
// stands above both sections; the training-strand SELECTION is an operator control of the training page
// (D-HP-ADMIN-072) — here it is shown, not switched. Every piece of content comes from the payload, a
// payload without `strands` falls back to the previous single-strand rendering, and a missing document or
// an empty list is said so in plain words. The dashboard chrome stays in all twelve languages.
const ARCHITECTURE_ENDPOINT = '/api/admin/veridion-core/quantum-architecture';
const AUTOPILOT_ENDPOINT = '/api/admin/datalake/autopilot';
const READINESS_ENDPOINT = '/api/admin/datalake/readiness';
const OVERVIEW_ENDPOINT = '/api/admin/datalake/overview';
// D-HP-ADMIN-075: the kernel probe's report, the raw-text corpus facts and the sealed versions' measured
// token masses — every one feature-detected, every absence said plainly.
const KERNEL_ENDPOINT = '/api/admin/datalake/gpu/kernel-report';
const CORPUS_ENDPOINT = '/api/admin/datalake/corpus';
const TDV_ENDPOINT = '/api/admin/datalake/tdv';

// A capability threshold applies to a strand when its modality signature (`t->t`, `ti->t`, …) fits
// the strand's declared input and output modalities (D-SAAS-146 §3) — derived from the spec, so every
// strand's strip judges its own thresholds, not only the selected one.
const MODALITY_LETTERS = { text: 't', image: 'i', audio: 'a', video: 'v' };
const modalityLetters = (names) => new Set((names || []).map((name) => MODALITY_LETTERS[name]).filter(Boolean));
const modSigApplies = (modSig, modsIn, modsOut) => {
  const [left, right] = String(modSig || 't->t').split('->');
  const inputs = modalityLetters(modsIn);
  const outputs = modalityLetters(modsOut);
  return [...(left || '')].every((ch) => inputs.has(ch)) && [...(right || left || '')].every((ch) => outputs.has(ch));
};
const splitThresholds = (block, thresholds) => {
  const iface = block?.current?.spec?.data_interface || {};
  const modsIn = iface.modalities_in || ['text'];
  const modsOut = iface.modalities_out || ['text'];
  const applicable = [];
  const foreign = [];
  (thresholds || []).forEach((row) => (modSigApplies(row.mod_sig, modsIn, modsOut) ? applicable : foreign).push(row));
  return { applicable, foreign };
};
const formatCount = (value, lang) => (value === null || value === undefined ? '—' : Number(value).toLocaleString(lang));

// A paper field is a plain English string since the document became English-only (D-HP-ADMIN-065); a
// legacy bilingual object ({ de, en }) still resolves to its English side.
const enText = (field) => {
  if (field === null || field === undefined) return '';
  if (typeof field !== 'object') return String(field);
  return String(field.en ?? field.de ?? '');
};

// Document-internal labels: the paper is an English-only scientific document, so every label inside the
// document is English regardless of the dashboard language; only the chrome around it is localized.
const DOC = {
  table: 'Table', stage: 'Stage', mechanism: 'Mechanism', params: 'Parameters (B)',
  component: 'Component', training: 'Training', inference: 'Inference',
  budget: 'Total (budget)', limit: 'Limit', precision: 'Precision',
  modelParams: 'Model parameters', name: 'Name', value: 'Value', formula: 'Formula',
  property: 'Property', tokenSpaces: 'Token spaces', space: 'Space', idRange: 'ID range',
  codec: 'Codec', state: 'State', released: 'released', draft: 'draft — not yet built',
  objectives: 'Training objectives', modsIn: 'Input modalities', modsOut: 'Output modalities',
  context: 'Context length (tokens)', interleaving: 'Interleaving', position: 'Position scheme',
  sync: 'Modality synchronisation', yes: 'required', no: 'no',
  vocab: 'Vocabulary size', tokenizerRow: 'Tokenizer', tokenizerVersion: 'Tokenizer version',
  controlTokens: 'Control tokens', benchmark: 'Benchmark', suite: 'Suite', score: 'Score',
  evaluator: 'Evaluator', date: 'Date', version: 'Version', codename: 'Codename', status: 'Status',
  summary: 'Summary', abstract: 'Abstract', preliminaryRemark: 'Preliminary remark',
  references: 'References', appendix: 'Appendix',
  figure: 'Figure', empty: 'No entries.',
  abbreviation: 'Abbreviation', meaning: 'Meaning', modality: 'Modality',
  geometry: 'Geometry', releasedCol: 'Released artifact',
  division: 'Division of work', kernel: 'Kernel', proof: 'Proof', step: 'Step',
  work: 'Work', decision: 'Decision', where: 'Where it enters', why: 'Why', deliberation: 'Deliberation',
  provenanceCaption: 'Related work and design choices: what each work contributed, where it enters, and why.',
  tableCaption: 'Data table.',
  kernelsCaption: 'The kernel program mapped onto the architecture components.',
  stagesCaption: 'The architecture stages with their mechanisms.',
  trainingCaption: 'Training VRAM budget by component.',
  inferenceCaption: 'Inference VRAM budget by component.',
  paramsCaption: 'Derived model parameters with their formulas.',
  interfaceCaption: 'The data interface of the architecture.',
  controlsCaption: 'Control tokens of the shared tokenizer, in their released literal notation.',
  controlToken: 'Control token',
  controlId: 'Id (released)',
  spacesCaption: 'Token spaces of the unified vocabulary.',
  continuousCaption: 'Continuous (non-token) inputs and their deterministic codecs.',
  evidenceCaption: 'Measured evaluations of the active architecture.',
  changelogCaption: 'Architecture versions of the evolution history.',
};

const COMPONENT_NAMES = {
  weights: 'Weights',
  optimizer: 'Optimizer',
  activations: 'Activations',
  buffers: 'Buffers',
  kv_cache: 'KV cache',
};

const gb = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '—');

// Jump to a reference without touching the location hash: the dashboard routes on the hash (#llm), so a
// plain anchor link would re-route the whole admin page instead of scrolling.
const scrollToReference = (n) => {
  const target = document.getElementById(`llm-ref-${n}`);
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

// The superscript citation markers of a stage (`refs: [n]`), linked to the references list.
const RefMarks = ({ refs }) => {
  const numbers = (Array.isArray(refs) ? refs : []).filter((n) => Number.isFinite(Number(n)));
  if (!numbers.length) return null;
  return (
    <sup className="llm-paper-sup">
      [{numbers.map((n, index) => (
        <React.Fragment key={n}>
          {index > 0 ? ', ' : ''}
          <button type="button" className="llm-paper-ref-link" onClick={() => scrollToReference(n)}>{n}</button>
        </React.Fragment>
      ))}]
    </sup>
  );
};

// A booktabs table with its caption above, numbered through the document.
// A table without a number is an unnumbered overview (the interface's property list): the document numbers
// only the tables it names in `table_captions`, so the web and the PDF count the same tables (D-VCORE-365).
const PaperTable = ({ number, caption, head, children }) => (
  <div className="llm-paper-table-block">
    <div className="llm-paper-tabcaption">{number ? `${DOC.table} ${number}: ${caption}` : caption}</div>
    <table className="llm-paper-table">
      <thead><tr>{head.map((label, i) => <th key={i} className={i > 0 ? 'llm-paper-num' : ''}>{label}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

// The document assigns every table number itself since D-VCORE-351 (`table_captions`/`caption`
// records with {number, caption}); a legacy payload without them keeps the local counter.
const captionOf = (record, fallbackNumber, fallbackCaption) => ({
  number: record?.number ?? fallbackNumber,
  caption: enText(record?.caption) || fallbackCaption,
});

// Section 2.1 — the stages (rows: name/mechanism/layers, params_physical_b with its honest note, refs).
const StagesTable = ({ rows, number, caption }) => (
  <PaperTable number={number} caption={caption || DOC.stagesCaption} head={[DOC.stage, DOC.mechanism, DOC.params]}>
    {rows.map((row, i) => (
      <tr key={row.id || i}>
        <td className="llm-paper-stagecell">
          <span className="llm-paper-strong">{enText(row.name)}</span><RefMarks refs={row.refs} />
          {enText(row.layers) ? <span className="llm-paper-sub">{enText(row.layers)}</span> : null}
        </td>
        <td>{enText(row.mechanism)}</td>
        <td className="llm-paper-num">
          {row.params_physical_b ?? (enText(row.params_note)
            ? <span className="llm-paper-sub">{enText(row.params_note)}</span> : '—')}
        </td>
      </tr>
    ))}
  </PaperTable>
);

// Section 1.1 — the generated nomenclature (D-VCORE-351 §2): only abbreviations the document uses.
const NomenclatureTable = ({ rows }) => (
  <table className="llm-paper-table">
    <thead><tr><th>{DOC.abbreviation}</th><th>{DOC.meaning}</th></tr></thead>
    <tbody>
      {rows.map((row, i) => (
        <tr key={row.abbr || i}>
          <td className="llm-paper-strong">{row.abbr}</td>
          <td>{enText(row.meaning)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// The end-notes (D-VCORE-351 §1): the recorded mechanisms behind the superscript markers.
const NotesList = ({ notes }) => (
  <ol className="llm-paper-references">
    {notes.map((note) => (
      <li key={note.n || note.id} value={note.n}>{note.n}. {enText(note.text)}</li>
    ))}
  </ol>
);

// Section 3 — one budget table (training or inference) plus the derived-parameters table.
const BudgetTable = ({ block, title, caption, number }) => {
  const breakdown = block?.breakdown || {};
  return (
    <PaperTable number={number} caption={caption} head={[`${DOC.component} (${title})`, 'GB']}>
      {Object.entries(breakdown).map(([key, value]) => {
        const name = key.replace(/_gb$/, '');
        return (
          <tr key={key}>
            <td>{COMPONENT_NAMES[name] || name.replace(/_/g, ' ')}</td>
            <td className="llm-paper-num">{gb(value)}</td>
          </tr>
        );
      })}
      <tr className="llm-paper-totalrow">
        <td>{DOC.budget}</td>
        <td className="llm-paper-num">{gb(block?.budget_gb)}</td>
      </tr>
      <tr>
        <td>{DOC.limit}{enText(block?.limit_note) ? <span className="llm-paper-sub">{enText(block.limit_note)}</span> : null}</td>
        <td className="llm-paper-num">{gb(block?.limit_gb)}</td>
      </tr>
      {block?.precision ? <tr><td>{DOC.precision}</td><td className="llm-paper-num">{block.precision}</td></tr> : null}
    </PaperTable>
  );
};

const ParametersTable = ({ parameters, number, caption }) => (
  <PaperTable number={number} caption={caption || DOC.paramsCaption} head={[DOC.name, DOC.value, DOC.formula]}>
    {(parameters || []).map((row, i) => (
      <tr key={row.name || i}>
        <td>{row.name}</td>
        <td className="llm-paper-num">{row.value ?? '—'}</td>
        <td>
          {row.formula}
          {row.inputs && Object.keys(row.inputs).length
            ? <span className="llm-paper-sub">{Object.entries(row.inputs).map(([k, v]) => `${k} = ${v}`).join(', ')}</span>
            : null}
        </td>
      </tr>
    ))}
  </PaperTable>
);

const VramSection = ({ data, numbers }) => {
  const captions = data?.captions || {};
  const training = captionOf(captions.vram_training, numbers[0], DOC.trainingCaption);
  const inference = captionOf(captions.vram_inference, numbers[1], DOC.inferenceCaption);
  const parameters = captionOf(captions.vram_parameters, numbers[2], DOC.paramsCaption);
  return (
    <>
      <BudgetTable block={data?.training} title={DOC.training} caption={training.caption} number={training.number} />
      {data?.training?.formula ? <p className="llm-paper-formula">{data.training.formula}</p> : null}
      <BudgetTable block={data?.inference} title={DOC.inference} caption={inference.caption} number={inference.number} />
      {data?.inference?.formula ? <p className="llm-paper-formula">{data.inference.formula}</p> : null}
      {Array.isArray(data?.parameters) && data.parameters.length
        ? <ParametersTable parameters={data.parameters} number={parameters.number} caption={parameters.caption} /> : null}
    </>
  );
};

// Section 4 — the data interface: a properties table, the token-space table and the honest status note.
const objectiveText = (objectives) => (Array.isArray(objectives) ? objectives : []).map((o) => {
  const params = Object.entries(o?.params || {}).filter(([, v]) => v !== null && v !== undefined)
    .map(([k, v]) => `${k}: ${v}`).join(', ');
  return params ? `${o.name} (${params})` : o?.name;
}).filter(Boolean).join(' · ');

const controlTokenText = (tokens) => {
  if (Array.isArray(tokens)) return tokens.map((t) => (typeof t === 'object' ? t?.name : t)).filter(Boolean).join(', ');
  if (tokens && typeof tokens === 'object') return Object.keys(tokens).join(', ');
  return '';
};

const InterfaceSection = ({ data, numbers }) => {
  const tokenizer = data?.tokenizer || {};
  const sequence = data?.sequence || {};
  const list = (value) => (Array.isArray(value) ? value.join(', ') : '');
  const rows = [
    [DOC.tokenizerRow, tokenizer.tokenizer_id || '—'],
    [DOC.tokenizerVersion, tokenizer.tokenizer_version || DOC.draft],
    [DOC.vocab, tokenizer.vocab_size_total ? Number(tokenizer.vocab_size_total).toLocaleString('en-GB') : '—'],
    [DOC.controlTokens, controlTokenText(tokenizer.control_tokens) || '—'],
    [DOC.modsIn, list(data?.modalities_in) || '—'],
    [DOC.modsOut, list(data?.modalities_out) || '—'],
    [DOC.context, data?.context_length_tokens ? Number(data.context_length_tokens).toLocaleString('en-GB') : '—'],
    [DOC.interleaving, sequence.interleaving || '—'],
    [DOC.position, sequence.position_scheme || '—'],
    [DOC.sync, sequence.sync_required === true ? DOC.yes : sequence.sync_required === false ? DOC.no : '—'],
    [DOC.objectives, objectiveText(data?.objectives) || '—'],
  ];
  const spaces = Array.isArray(tokenizer.token_spaces) ? tokenizer.token_spaces : [];
  const continuous = Array.isArray(data?.continuous_inputs) ? data.continuous_inputs : [];
  const captions = data?.captions || {};
  const spacesCap = captionOf(captions.iface_spaces, numbers[0], DOC.spacesCaption);
  const continuousCap = captionOf(captions.iface_continuous, numbers[1], DOC.continuousCaption);
  const controlsCap = captionOf(captions.iface_controls, continuous.length ? numbers[2] : numbers[1], DOC.controlsCaption);
  const controls = Array.isArray(tokenizer.control_tokens) ? tokenizer.control_tokens : [];
  return (
    <>
      <PaperTable number={null} caption={DOC.interfaceCaption} head={[DOC.property, DOC.value]}>
        {rows.map(([property, value]) => <tr key={property}><td>{property}</td><td>{value}</td></tr>)}
      </PaperTable>
      {spaces.length ? (
        <PaperTable number={spacesCap.number} caption={spacesCap.caption}
          head={[DOC.space, DOC.idRange, DOC.codec, DOC.state]}>
          {spaces.map((space, i) => {
            const released = space.space === 'text'
              ? Boolean(tokenizer.tokenizer_sha256) : Boolean(space.codec_sha256);
            return (
              <tr key={space.space || i}>
                <td>{space.space}</td>
                <td className="llm-paper-num">{space.id_start ?? '—'} – {space.id_end_excl ?? '—'}</td>
                <td>{space.codec_id || (space.space === 'text' ? tokenizer.tokenizer_id : '—')}</td>
                <td>{released ? DOC.released : DOC.draft}</td>
              </tr>
            );
          })}
        </PaperTable>
      ) : null}
      {continuous.length ? (
        <PaperTable number={continuousCap.number} caption={continuousCap.caption}
          head={[DOC.modality, DOC.codec, DOC.releasedCol, DOC.geometry]}>
          {continuous.map((entry, i) => (
            <tr key={entry.codec_id || i}>
              <td>{entry.modality}</td>
              <td>{entry.codec_id}{entry.codec_version ? ` (${entry.codec_version})` : ''}</td>
              <td>{entry.codec_sha256 ? DOC.released : DOC.draft}</td>
              <td className="llm-paper-sub">
                {['patch_size_px', 'pixel_merge', 'max_patches_per_image', 'normalization']
                  .filter((key) => entry[key] !== undefined && entry[key] !== null)
                  .map((key) => `${key} = ${entry[key]}`).join(', ') || '—'}
              </td>
            </tr>
          ))}
        </PaperTable>
      ) : null}
      {controls.length ? (
        <PaperTable number={controlsCap.number} caption={controlsCap.caption} head={[DOC.controlToken, DOC.controlId]}>
          {controls.map((token, i) => (
            <tr key={token.token || i}>
              <td className="font-mono text-xs">{token.token}</td>
              <td className="llm-paper-num">{token.id ?? DOC.draft}</td>
            </tr>
          ))}
        </PaperTable>
      ) : null}
      {enText(data?.status_note) ? <p className="llm-paper-statusnote">{enText(data.status_note)}</p> : null}
    </>
  );
};

// Section 5 — the honest evidence state: the note when nothing is measured, a table once evaluations exist.
const EvidenceSection = ({ data, number }) => {
  const evaluations = Array.isArray(data?.evaluations) ? data.evaluations : [];
  if (!evaluations.length) {
    return <p className="llm-paper-statusnote">{enText(data?.note ?? data?.note_en)}</p>;
  }
  return (
    <PaperTable number={number} caption={DOC.evidenceCaption}
      head={[DOC.benchmark, DOC.suite, DOC.score, DOC.evaluator, DOC.date]}>
      {evaluations.map((row, i) => (
        <tr key={i}>
          <td>{row.benchmark_id || row.metric || '—'}</td>
          <td>{row.suite_version || '—'}</td>
          <td className="llm-paper-num">{row.score ?? '—'}</td>
          <td>{row.evaluator || '—'}</td>
          <td>{row.created_at ? String(row.created_at).slice(0, 10) : '—'}</td>
        </tr>
      ))}
    </PaperTable>
  );
};

// Section 3.1 — the kernel program mapped onto the components (D-HP-ADMIN-076): division of work on the
// card, kernel, proof and the measured or declared status; refs resolve like the stages' sources.
const KernelTable = ({ rows, number, caption }) => (
  <PaperTable number={number} caption={caption || DOC.kernelsCaption}
    head={[DOC.component, DOC.division, DOC.kernel, DOC.proof, DOC.status]}>
    {rows.map((row, i) => (
      <tr key={row.component || i}>
        <td className="llm-paper-stagecell"><span className="llm-paper-strong">{enText(row.component)}</span><RefMarks refs={row.refs} /></td>
        <td>{enText(row.division)}</td>
        <td className="llm-paper-kernelcell">{enText(row.kernel)}</td>
        <td>{enText(row.proof)}</td>
        <td>{enText(row.status)}</td>
      </tr>
    ))}
  </PaperTable>
);

// The training-data chain as numbered steps (D-HP-ADMIN-076), each with the works that ground it (D-VCORE-362).
const StepsList = ({ steps }) => (
  <ol className="llm-paper-steps">
    {steps.map((step, i) => (
      <li key={step.n || i}>
        <span className="llm-paper-strong">{DOC.step} {step.n || i + 1} — {enText(step.title)}<RefMarks refs={step.refs} />.</span> {enText(step.body)}
      </li>
    ))}
  </ol>
);

// The research provenance table (D-HP-ADMIN-079): work, decision, place, reason and deliberation.
const ProvenanceTable = ({ rows, number, caption }) => (
  <PaperTable number={number} caption={caption || DOC.provenanceCaption}
    head={[DOC.work, DOC.decision, DOC.where, DOC.why]}>
    {rows.map((row, i) => (
      <tr key={`${row.work}-${i}`}>
        <td className="llm-paper-stagecell"><span className="llm-paper-strong">{enText(row.work)}</span><RefMarks refs={row.refs} /></td>
        <td>{enText(row.decision)}</td>
        <td>{enText(row.where)}</td>
        <td>{enText(row.why)}</td>
      </tr>
    ))}
  </PaperTable>
);

// A data table of the document (D-VCORE-365 §2): the generator states columns and rows, both renderers show them.
const GenericTable = ({ table, number }) => {
  const columns = Array.isArray(table?.columns) ? table.columns : [];
  const rows = Array.isArray(table?.rows) ? table.rows : [];
  if (!columns.length) return null;
  const cap = captionOf(table?.caption, number, DOC.tableCaption);
  return (
    <PaperTable number={cap.number} caption={cap.caption} head={columns.map((column) => enText(column))}>
      {rows.map((row, i) => (
        <tr key={i}>
          {columns.map((_, j) => (
            <td key={j} className={j > 0 ? 'llm-paper-num' : ''}>{enText(Array.isArray(row) ? row[j] : '')}</td>
          ))}
        </tr>
      ))}
    </PaperTable>
  );
};

// The end-to-end pipeline figure (D-HP-ADMIN-079, D-VCORE-362 §6): drawn as an inline SVG from the payload's
// lanes and blocks — the same structure the server-typeset PDF draws. Solid blocks rest on reported
// measurements; dashed blocks are declared or not yet reached.
const PIPE = { width: 720, laneGap: 16, blockGap: 14, pad: 6, header: 22 };
const wrapWords = (text, limit) => {
  const lines = [];
  let line = '';
  String(text || '').split(/\s+/).filter(Boolean).forEach((word) => {
    if (line && line.length + 1 + word.length > limit) { lines.push(line); line = word; }
    else line = `${line} ${word}`.trim();
  });
  if (line) lines.push(line);
  return lines;
};
const pipelineLines = (block, chars) => {
  const lines = wrapWords(enText(block?.title), chars - 3).map((text) => ({ text, size: 11, weight: 700 }));
  wrapWords(enText(block?.detail), chars).forEach((text) => lines.push({ text, size: 9.5 }));
  if (block?.fact) wrapWords(enText(block.fact), chars).forEach((text) => lines.push({ text, size: 9.5, italic: true }));
  (Array.isArray(block?.units) ? block.units : []).forEach((unit) => wrapWords(enText(unit), chars - 2)
    .forEach((text, index) => lines.push({ text: `${index === 0 ? '– ' : '  '}${text}`, size: 9 })));
  return lines;
};
const PipelineFigure = ({ figure }) => {
  const lanes = Array.isArray(figure?.lanes) ? figure.lanes : [];
  if (!lanes.length) return null;
  const laneW = (PIPE.width - (lanes.length - 1) * PIPE.laneGap) / lanes.length;
  const chars = Math.max(Math.floor((laneW - 2 * PIPE.pad) / 5.3), 16);
  const laid = lanes.map((lane) => (Array.isArray(lane.blocks) ? lane.blocks : []).map((block) => {
    const lines = pipelineLines(block, chars);
    return { block, lines, height: 2 * PIPE.pad + lines.reduce((sum, line) => sum + line.size * 1.38, 0) };
  }));
  const laneHeights = laid.map((column) => column.reduce((sum, item) => sum + item.height, 0)
    + PIPE.blockGap * Math.max(column.length - 1, 0));
  const height = PIPE.header + Math.max(0, ...laneHeights) + 6;
  const shapes = [];
  const anchors = [];
  laid.forEach((column, laneIndex) => {
    const x = laneIndex * (laneW + PIPE.laneGap);
    shapes.push(<text key={`h${laneIndex}`} x={x + laneW / 2} y={14} textAnchor="middle" fontWeight="700"
      fontSize="11.5" fill="#1a1a1a">{enText(lanes[laneIndex]?.title)}</text>);
    let top = PIPE.header;
    let first = null;
    let last = null;
    column.forEach((item, blockIndex) => {
      const measured = item.block?.state === 'measured';
      shapes.push(<rect key={`r${laneIndex}-${blockIndex}`} x={x} y={top} width={laneW} height={item.height}
        fill={measured ? '#f4f4f4' : '#ffffff'} stroke={measured ? '#1a1a1a' : '#8d8d8d'} strokeWidth="1"
        strokeDasharray={measured ? undefined : '4 3'} />);
      let cursor = top + PIPE.pad;
      item.lines.forEach((line, lineIndex) => {
        cursor += line.size * 1.38;
        shapes.push(<text key={`t${laneIndex}-${blockIndex}-${lineIndex}`} x={x + PIPE.pad} y={cursor - line.size * 0.35}
          fontSize={line.size} fontWeight={line.weight || 400} fontStyle={line.italic ? 'italic' : 'normal'}
          fill={measured ? '#1a1a1a' : '#525252'} xmlSpace="preserve">{line.text}</text>);
      });
      if (!first) first = [x, top + item.height / 2];
      last = [x + laneW, top + item.height / 2];
      if (blockIndex < column.length - 1) {
        const ax = x + laneW / 2;
        shapes.push(<line key={`a${laneIndex}-${blockIndex}`} x1={ax} y1={top + item.height} x2={ax} y2={top + item.height + PIPE.blockGap - 4} stroke="#1a1a1a" strokeWidth="1" />);
        shapes.push(<polygon key={`p${laneIndex}-${blockIndex}`} points={`${ax - 3},${top + item.height + PIPE.blockGap - 5} ${ax + 3},${top + item.height + PIPE.blockGap - 5} ${ax},${top + item.height + PIPE.blockGap}`} fill="#1a1a1a" />);
      }
      top += item.height + PIPE.blockGap;
    });
    anchors.push([first || [x, height / 2], last || [x + laneW, height / 2]]);
  });
  anchors.slice(0, -1).forEach((pair, index) => {
    const [x1, y1] = pair[1];
    const [x2, y2] = anchors[index + 1][0];
    const middle = x1 + PIPE.laneGap / 2;
    shapes.push(<polyline key={`c${index}`} points={`${x1},${y1} ${middle},${y1} ${middle},${y2} ${x2 - 5},${y2}`} fill="none" stroke="#1a1a1a" strokeWidth="1" />);
    shapes.push(<polygon key={`ch${index}`} points={`${x2 - 6},${y2 - 3} ${x2 - 6},${y2 + 3} ${x2},${y2}`} fill="#1a1a1a" />);
  });
  return (
    <svg className="llm-paper-pipeline" viewBox={`0 0 ${PIPE.width} ${height}`} width="100%" role="img"
      aria-label={enText(figure?.caption)} fontFamily="Helvetica, Arial, sans-serif">
      {shapes}
    </svg>
  );
};

// Section 7 (appendix) — the changelog of architecture versions.
const ChangelogTable = ({ rows, number }) => (
  <PaperTable number={number} caption={DOC.changelogCaption}
    head={[DOC.version, DOC.codename, DOC.status, DOC.summary, DOC.date]}>
    {(rows || []).map((row, i) => (
      <tr key={row.version || i}>
        <td className="llm-paper-num">{row.version}</td>
        <td>{row.codename}</td>
        <td>{row.status}</td>
        <td>{enText(row.summary_en ?? row.summary)}</td>
        <td>{row.created_at ? String(row.created_at).slice(0, 10) : '—'}</td>
      </tr>
    ))}
  </PaperTable>
);

// The pure paper document, rendered from the payload alone (exported for the render test harness). The
// document is English-only (D-HP-ADMIN-065) with en-GB numbers and dates; `p` stays in the signature for
// the harness, the localized chrome lives outside the document.
export const PaperDocument = ({ payload, p }) => {
  const paper = payload?.paper || null;
  const diagram = payload?.current?.mermaid_diagram || '';
  if (!paper) return null;
  const references = (Array.isArray(paper.references) ? paper.references : [])
    .slice().sort((left, right) => (left.n || 0) - (right.n || 0));
  const sections = Array.isArray(paper.sections) ? paper.sections : [];
  const designNote = enText(paper.design_figures_label);
  const dateLabel = paper.date_iso && !Number.isNaN(Date.parse(paper.date_iso))
    ? new Date(paper.date_iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
    : (paper.date_iso || '');
  let figureNumber = 0;
  let tableNumber = 0;
  const nextTables = (count) => Array.from({ length: count }, () => { tableNumber += 1; return tableNumber; });
  return (
    <article className="llm-paper">
      <h1 className="llm-paper-title">{enText(paper.title)}</h1>
      <div className="llm-paper-authors">
        {(Array.isArray(paper.authors) ? paper.authors : []).map((author, index) => (
          <div key={index} className="llm-paper-author">
            <div className="llm-paper-author-name">{author?.name}</div>
            {author?.affiliation ? <div className="llm-paper-author-affiliation">{author.affiliation}</div> : null}
          </div>
        ))}
      </div>
      {dateLabel ? <div className="llm-paper-dateline">{dateLabel}</div> : null}
      {enText(paper.preliminary_remark) ? (
        <>
          <h2 className="llm-paper-abstract-heading">{DOC.preliminaryRemark}</h2>
          <div className="llm-paper-abstract">
            {String(enText(paper.preliminary_remark)).split(/\n\s*\n/).map((text, i) => <p key={i}>{text.trim()}</p>)}
          </div>
        </>
      ) : null}
      {enText(paper.abstract) ? (
        <>
          <h2 className="llm-paper-abstract-heading">{DOC.abstract}</h2>
          <div className="llm-paper-abstract">
            {String(enText(paper.abstract)).split(/\n\s*\n/).map((text, i) => <p key={i}>{text.trim()}</p>)}
          </div>
        </>
      ) : null}
      {sections.map((section) => {
        const number = String(section?.number ?? '');
        const heading = enText(section?.heading);
        const body = enText(section?.body);
        const isAppendix = /^[A-Za-z]/.test(number);
        if (section?.kind === 'mermaid' || section?.kind === 'pipeline_figure') figureNumber += 1;
        return (
          <section key={section?.id || number}>
            <h2 className="llm-paper-section-heading">
              <span className="llm-paper-secnum">{isAppendix ? `${DOC.appendix} ${number}` : number}</span>
              {heading}
            </h2>
            {body ? (
              <div className="llm-paper-body">
                {String(body).split(/\n\s*\n/).map((text, i) => <p key={i}>{text.trim()}</p>)}
              </div>
            ) : null}
            {section?.kind === 'mermaid' ? (
              diagram ? (
                <figure className="llm-paper-figure">
                  <MermaidDiagram chart={diagram} />
                  <figcaption className="llm-paper-figure-caption">{DOC.figure} {figureNumber}: {heading}</figcaption>
                </figure>
              ) : <p className="llm-paper-empty">{DOC.empty}</p>
            ) : null}
            {section?.kind === 'pipeline_figure' && section?.data ? (
              <figure className="llm-paper-figure">
                <PipelineFigure figure={section.data} />
                <figcaption className="llm-paper-figure-caption">{DOC.figure} {figureNumber}: {enText(section.data.caption)}</figcaption>
              </figure>
            ) : null}
            {section?.kind === 'provenance' ? (() => {
              const rows = Array.isArray(section?.data?.rows) ? section.data.rows : [];
              const cap = captionOf(section?.data?.caption, nextTables(1)[0], DOC.provenanceCaption);
              return rows.length ? <ProvenanceTable rows={rows} number={cap.number} caption={cap.caption} /> : null;
            })() : null}
            {section?.kind === 'stages_table' ? (() => {
              // D-VCORE-351: the data carries {rows, caption}; a legacy payload is the bare list.
              const rows = Array.isArray(section?.data) ? section.data
                : (Array.isArray(section?.data?.rows) ? section.data.rows : []);
              const cap = captionOf(section?.data?.caption, nextTables(1)[0], DOC.stagesCaption);
              return rows.length ? <StagesTable rows={rows} number={cap.number} caption={cap.caption} /> : null;
            })() : null}
            {section?.kind === 'nomenclature' && Array.isArray(section?.data) && section.data.length
              ? <NomenclatureTable rows={section.data} /> : null}
            {section?.kind === 'notes' && Array.isArray(section?.data) && section.data.length
              ? <NotesList notes={section.data} /> : null}
            {section?.kind === 'vram_table' && section?.data
              ? <VramSection data={section.data} numbers={nextTables(3)} /> : null}
            {section?.kind === 'kernel_table' ? (() => {
              const rows = Array.isArray(section?.data?.rows) ? section.data.rows : [];
              const cap = captionOf(section?.data?.caption, nextTables(1)[0], DOC.kernelsCaption);
              return rows.length ? <KernelTable rows={rows} number={cap.number} caption={cap.caption} /> : null;
            })() : null}
            {section?.kind === 'steps' && Array.isArray(section?.data) && section.data.length
              ? <StepsList steps={section.data} /> : null}
            {section?.kind === 'interface_table' && section?.data
              ? <InterfaceSection data={section.data} numbers={nextTables(3)} /> : null}
            {section?.kind === 'evidence' && section?.data
              ? <EvidenceSection data={section.data}
                  number={Array.isArray(section?.data?.evaluations) && section.data.evaluations.length ? nextTables(1)[0] : 0} /> : null}
            {section?.kind === 'table' && Array.isArray(section?.data?.tables)
              ? section.data.tables.map((table, i) => <GenericTable key={table?.slot || i} table={table} number={nextTables(1)[0]} />)
              : null}
            {section?.kind === 'changelog' && Array.isArray(section?.data)
              ? <ChangelogTable rows={section.data} number={nextTables(1)[0]} /> : null}
          </section>
        );
      })}
      {designNote ? <div className="llm-paper-note">{designNote}</div> : null}
      {references.length ? (
        <section>
          <h2 className="llm-paper-section-heading">{DOC.references}</h2>
          <ol className="llm-paper-references">
            {references.map((reference) => (
              <li key={reference.n || reference.pub_id} id={`llm-ref-${reference.n}`} value={reference.n}>
                [{reference.n}] {reference.citation_text}
                {reference.url ? <> <a href={reference.url} target="_blank" rel="noreferrer">{String(reference.url).replace(/^https?:\/\//, '')}</a></> : null}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      <div className="llm-paper-provenance">
        {/* D-HP-ADMIN-091 §1: the model, never a version */}
        {paper.codename ? `${paper.codename} · ` : ''}pAIoneer Labs GmbH
      </div>
    </article>
  );
};

// --- The monitoring section (D-HP-ADMIN-065): the operational view beside the paper, restoring the
// --- observability D-HP-ADMIN-062 removed. It renders the same payload's operational fields in the
// --- dashboard's own styling; bilingual pairs (`field` beside `field_en`) follow the dashboard language
// --- (German reads the German side, every other language the English side) and empty states are honest.

const pickPair = (source, key, isDe) => {
  const base = source?.[key];
  const en = source?.[`${key}_en`];
  const value = isDe ? (base ?? en) : (en ?? base);
  return value === null || value === undefined ? '' : String(value);
};

// A readable label derived from an operational payload key (payload data, not UI chrome).
const factLabel = (key) => {
  const words = key.replace(/_en$/, '').replace(/_mib$/, '').replace(/_/g, ' ')
    .replace(/\bvram\b/gi, 'VRAM').replace(/\bgpu\b/gi, 'GPU').replace(/\bvllm\b/gi, 'vLLM');
  return words.charAt(0).toUpperCase() + words.slice(1) + (key.endsWith('_mib') ? ' (MiB)' : '');
};

const monitorValue = (value, lang) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return Number(value).toLocaleString(lang);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(' · ');
  return String(value);
};

// The measured facts of `gpu_vram_budget`, each under its own label; the older duplicate fields stay unshown.
const vramFacts = (budget, isDe, lang, m) => {
  const gb = (value) => (value === null || value === undefined || value === '' ? '—'
    : `${Number(value).toLocaleString(lang, { maximumFractionDigits: 1 })} GB`);
  return [
    [m.gpuModel, monitorValue(budget.gpu_model, lang)],
    [m.gpuTotal, budget.total_vram_mib ? gb(Number(budget.total_vram_mib) / 1024) : '—'],
    [m.gpuWindow, gb(budget.training_window_gb)],
    [m.gpuMeasuredAt, budget.training_window_measured_at ? formatBerlin(budget.training_window_measured_at) : '—'],
    [m.gpuServing, pickPair(budget, 'serving_status', isDe) || '—'],
    [m.gpuInferenceTarget, gb(budget.single_gpu_inference_target_gb)],
  ];
};

// The per-strand implementation status (D-HP-ADMIN-071): measured facts only — the spec's artifact
// state, the strand's sample progress from the autopilot thresholds, the selection and the freeze.
const ImplementationPanel = ({ block, payload, autopilot, corpus, m, isDe, lang }) => {
  const current = block?.current || {};
  const spec = current.spec || {};
  const tokenizer = spec?.data_interface?.tokenizer || {};
  const continuous = Array.isArray(spec?.data_interface?.continuous_inputs)
    ? spec.data_interface.continuous_inputs : [];
  const isTraining = payload?.training_strand === block?.strand;
  const facts = [
    [m.implSelected, isTraining ? m.implSelectedYes : m.implSelectedNo],
    [m.interfaceStatus, m[`stateWord_${current.data_interface_status}`] || current.data_interface_status || '—'],
    [m.implTokenizer, tokenizer.tokenizer_sha256
      ? `${tokenizer.tokenizer_version || ''} · ${String(tokenizer.tokenizer_sha256).slice(0, 12)}…`
      : m.implNotReleased],
  ];
  continuous.forEach((entry) => {
    facts.push([`${m.implCodec} (${entry.modality})`, entry.codec_sha256
      ? `${entry.codec_version || ''} · ${String(entry.codec_sha256).slice(0, 12)}…`
      : m.implNotReleased]);
  });
  if (corpus) {
    facts.push([m.implCorpus, corpus.tokens ? formatCount(corpus.tokens, lang) : m.kernelPlanNone]);
  }
  // The vector index as the lake reported it (D-SAAS-151 §3): a measured fact of the strand's chain.
  const index = block?.training_facts?.facts?.index;
  facts.push([m.implIndex, !index ? m.implIndexNone : !index.reachable ? m.implIndexDown
    : `${formatCount(index.points_count, lang)} ${m.implIndexPoints} · ${index.backfill_done ? m.implIndexBackfillDone : m.implIndexBackfillOpen}`]);
  return (
    <AdminSection id="llm-monitor-impl" title={m.implTitle} subtitle={m.implSubtitle}
      tone={isTraining ? 'ok' : ''}>
      <div className="space-y-3">
        <FactGrid items={facts} />
        <p className="text-sm"><a className="underline" href="/admin/datalake#datalake-index-dedup">{m.implIndexLink}</a></p>
        {isTraining ? <p className="text-sm"><a className="underline" href="/admin/datalake#datalake-situation">{m.implSituationLink}</a></p> : null}
        {isTraining && autopilot === null
          ? <p className="text-sm text-text-secondary">{m.implNoDatalake}</p> : null}
        <p className="text-sm text-text-secondary">{m.implSwitchHint}</p>
      </div>
    </AdminSection>
  );
};

// The implementation lifecycle under EACH paper (D-HP-ADMIN-073): one visible strip from the
// spec to the registry, every step a measured fact or an honest open state. Pipeline steps
// beyond the data gates belong to the SELECTED training strand; the other strand says plainly
// that it collects and prepares without starting (D-SAAS-125).
const LIFECYCLE_STAGE_IDS = ['trainer_image', 'sealed_tdv', 'compat_all_green', 'vram_budget'];

const StrandLifecycle = ({ name, block, payload, autopilot, readiness, overview, corpus, p, lang }) => {
  const current = block?.current || {};
  const spec = current.spec || {};
  const iface = spec?.data_interface || {};
  const tokenizer = iface.tokenizer || {};
  const continuous = Array.isArray(iface.continuous_inputs) ? iface.continuous_inputs : [];
  const selected = payload?.training_strand === (block?.strand || name);
  const frozen = block?.architecture_frozen === true;
  // D-HP-ADMIN-075 §2: only the thresholds this strand's modalities can consume count; the
  // others are shown as another strand's, never as this strand's open work.
  const allThresholds = Array.isArray(autopilot?.thresholds) ? autopilot.thresholds : [];
  const { applicable: thresholds, foreign } = splitThresholds(block, allThresholds);
  const stages = Array.isArray(readiness?.stages) ? readiness.stages : [];
  const stageState = (id) => String((stages.find((s) => s?.id === id) || {}).state || '');
  const runs = Array.isArray(overview?.runs) ? overview.runs : [];
  const exportRows = Array.isArray(overview?.exports) ? overview.exports : [];
  const evaluations = Array.isArray(block?.evaluations) ? block.evaluations : [];
  const metCount = thresholds.filter((row) => row.met === true).length;
  // D-HP-ADMIN-091 §2: the selected strand with a data plan is measured against the plan
  const planRows = ((stages.find((s) => s?.id === 'data_plan') || {}).plan || {}).categories || [];
  const planReached = planRows.filter((row) => row.met === true).length;
  const artifactsOk = Boolean(tokenizer.tokenizer_sha256)
    && continuous.every((entry) => Boolean(entry.codec_sha256));
  const pill = (state) => (state === 'ok' ? 'is-ok' : state === 'active' ? 'is-warning' : state === 'na' ? 'is-neutral' : 'is-missing');
  const stageWord = { ok: p.lcDone, active: p.lcActive, waiting: p.lcWaiting, blocked: p.lcBlocked };
  const steps = [
    [p.lcSpec, 'ok', `${current.codename || spec.codename || '—'}${frozen ? ` · ${p.lcFrozen}` : ''}`],
    [p.lcArtifacts, artifactsOk ? 'ok' : 'active',
      tokenizer.tokenizer_sha256 ? (artifactsOk ? p.lcReleased : p.lcCodecOpen) : p.lcNotReleased],
    planRows.length && selected
      ? [p.lcData, planReached === planRows.length ? 'ok' : 'active', fill(p.lcAreasReached, { met: planReached, total: planRows.length })]
      : [p.lcData, thresholds.length ? (metCount === thresholds.length ? 'ok' : 'active') : 'open',
        thresholds.length ? `${metCount}/${thresholds.length} ${p.lcThresholdsMet}` : p.lcNoLake],
    [p.lcCorpus, corpus && corpus.tokens ? 'ok' : (corpus ? 'active' : 'open'),
      corpus ? (corpus.tokens ? formatCount(corpus.tokens, lang) : p.lcCorpusNone) : p.lcNoLake],
  ];
  if (selected) {
    LIFECYCLE_STAGE_IDS.forEach((id) => {
      const state = stageState(id);
      steps.push([p[`lcStage_${id}`] || id, state === 'ok' ? 'ok' : state === 'active' ? 'active' : 'open',
        stageWord[state] || (readiness ? p.lcWaiting : p.lcNoLake)]);
    });
    // Only an active run is this strand's training; an aborted or failed run is history and never stands here.
    const activeRun = runs.find((row) => ['starting', 'running', 'interrupted', 'restoring']
      .includes(String(row?.state || row?.status || '')));
    steps.push([p.lcTraining, activeRun ? 'active' : 'open', activeRun ? p.lcActive : p.lcAuto]);
    steps.push([p.lcBench, evaluations.length ? 'ok' : 'open',
      evaluations.length ? `${evaluations.length} ${p.lcBenchRows}` : p.lcNoneYet]);
    steps.push([p.lcRelease, exportRows.length ? 'ok' : 'open',
      exportRows.length ? `${exportRows.length} ${p.lcReleaseRows}` : p.lcReleaseNote]);
  } else {
    steps.push([p.lcPipeline, 'na', p.lcCollectsOnly]);
  }
  return (
    <div className="llm-lifecycle homepage-muted-panel">
      <div className="llm-lifecycle-head">
        <span className="llm-paper-strong">{p.lcTitle}</span>
        <span className={`admin-status-pill ${selected ? 'is-ok' : 'is-neutral'}`}>
          {selected ? p.lcSelectedTrains : p.lcCollectsOnly}
        </span>
      </div>
      <div className="llm-lifecycle-steps">
        {steps.map(([label, state, detail], index) => (
          <div key={index} className="llm-lifecycle-step">
            <span className={`admin-status-pill ${pill(state)}`}>{label}</span>
            <span className="llm-lifecycle-detail">{detail}</span>
          </div>
        ))}
      </div>
      {thresholds.length || foreign.length ? (
        <table className="admin-table llm-lifecycle-table">
          <thead><tr><th>{p.lcCapability}</th><th>{p.lcSamples}</th><th>{p.lcByLanguage}</th></tr></thead>
          <tbody>
            {[...thresholds, ...foreign].map((row) => {
              const langRows = row.lang_samples && typeof row.lang_samples === 'object' ? row.lang_samples : {};
              const split = Object.entries(langRows).sort(([a], [b]) => a.localeCompare(b))
                .map(([code, count]) => `${code} ${Number(count).toLocaleString(lang)}`).join(' · ');
              const isForeign = foreign.includes(row);
              return (
                <tr key={`${row.capability}|${row.mod_sig}`} className={isForeign ? 'text-text-secondary' : ''}>
                  <td className="font-mono text-xs">{row.capability} ({row.mod_sig})
                    {isForeign ? <> <span className="admin-status-pill is-neutral">{p.lcForeign}</span></> : null}</td>
                  <td>{Number(row.measured_samples ?? 0).toLocaleString(lang)} / {Number(row.min_samples ?? 0).toLocaleString(lang)}{row.met === true ? ' ✓' : ''}</td>
                  <td className="text-xs">{split || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  );
};

// The kernel probe's report and the time plan (D-HP-ADMIN-075 §3, D-SAAS-148 §3): every number a
// measurement of the card or arithmetic over measurements; before the first probe the section says so.
const KernelSection = ({ kernel, corpus, tdvInfo, latestRun, m, lang, plan = null }) => {
  const report = kernel?.report || null;
  const throughput = report?.throughput || {};
  const tokensPerS = Number(throughput.tokens_per_s || latestRun?.tokens_per_s || 0);
  // D-SAAS-252 §2: with a data plan the time plan is the paper's token budget, not the corpus collected so far
  const budget = Number(plan?.duration?.budget_tokens_b || plan?.total_tokens_b || 0) * 1e9;
  const tokenMass = budget || tdvInfo?.token_total || (corpus?.tokens || 0);
  const tokenSource = budget ? m.kernelSourcePlan
    : tdvInfo?.token_total ? `${m.kernelSourceTdv} (${tdvInfo.tdv_id})` : (corpus?.tokens ? m.kernelSourceCorpus : null);
  const days = tokensPerS > 0 && tokenMass > 0 ? tokenMass / tokensPerS / 86400 : null;
  const runKernels = latestRun?.kernels_active && typeof latestRun.kernels_active === 'object' ? latestRun.kernels_active : null;
  return (
    <AdminSection id="llm-monitor-kernels" title={m.kernelTitle} subtitle={m.kernelSubtitle}
      tone={report ? 'ok' : ''}>
      <div className="space-y-3">
        {report ? (
          <>
            <table className="admin-table llm-kernel-table">
              <thead><tr><th>{m.kernelName}</th><th>{m.kernelVerified}</th><th>{m.kernelSpeedup}</th><th>{m.kernelMaxErr}</th><th>{m.kernelError}</th></tr></thead>
              <tbody>
                {(report.kernels || []).map((entry, index) => (
                  <tr key={entry.name || index}>
                    <td className="font-mono text-xs">{entry.name}</td>
                    <td>{entry.verified === true
                      ? <span className="admin-status-pill is-ok">{m.kernelYes}</span>
                      : <span className="admin-status-pill is-missing">{m.kernelNo}</span>}</td>
                    <td className="font-mono text-xs">{entry.speedup !== null && entry.speedup !== undefined ? `${Number(entry.speedup).toFixed(2)}×` : '—'}</td>
                    <td className="font-mono text-xs">{entry.max_abs_err !== null && entry.max_abs_err !== undefined ? Number(entry.max_abs_err).toExponential(2) : '—'}</td>
                    <td className="text-xs">{entry.error || entry.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <FactGrid items={[
              [m.kernelAttention, report.attention?.backend || '—'],
              [m.kernelDocMask, report.attention?.document_masking || '—'],
              [m.kernelThroughput, throughput.tokens_per_s ? formatCount(Math.round(throughput.tokens_per_s), lang) : m.kernelPlanNone],
              [m.kernelMicroBatch, throughput.micro_batch ?? '—'],
              [m.kernelMeasuredAt, report.measured_at ? formatBerlin(report.measured_at) : '—'],
              [m.kernelImage, report.image_digest ? String(report.image_digest).slice(0, 19) : '—'],
            ]} />
          </>
        ) : <p className="text-sm text-text-secondary">{kernel?.note_en || m.kernelNone}</p>}
        <div className="homepage-muted-panel p-3 space-y-2 text-sm">
          <div className="font-bold">{m.kernelRunPath}</div>
          {runKernels
            ? <p className="font-mono text-xs">{Object.entries(runKernels).map(([name, on]) => `${name}: ${on ? m.kernelYes : m.kernelNo}`).join(' · ')}
              {latestRun?.micro_batch ? ` · ${m.kernelMicroBatch} ${latestRun.micro_batch}` : ''}</p>
            : <p className="text-text-secondary">{m.kernelRunNone}</p>}
        </div>
        <div className="homepage-muted-panel p-3 space-y-2 text-sm">
          <div className="font-bold">{m.kernelPlanTitle}</div>
          {days !== null ? (
            <FactGrid items={[
              [m.kernelPlanTokens, formatCount(tokenMass, lang)],
              [m.kernelPlanTokensSource, tokenSource || '—'],
              [m.kernelPlanDays, Number(days.toFixed(1)).toLocaleString(lang)],
            ]} />
          ) : <p className="text-text-secondary">{m.kernelPlanNone}</p>}
        </div>
      </div>
    </AdminSection>
  );
};

const LlmMonitorView = ({ block, payload, autopilot, corpus, kernel, tdvInfo, latestRun, m, isDe, lang, plan = null }) => {
  const current = block?.current || {};
  const evidence = current.evidence || {};
  const evaluations = Array.isArray(evidence.evaluations) ? evidence.evaluations : [];
  const candidates = Array.isArray(block?.candidates) ? block.candidates : [];
  const changelog = Array.isArray(block?.changelog) ? block.changelog : [];
  const gate = block?.gate || payload?.gate || {};
  const metrics = Array.isArray(gate.required_metrics) ? gate.required_metrics : [];
  const weights = gate.weights || {};
  const budget = payload?.gpu_vram_budget || {};
  const frozen = block?.architecture_frozen === true;
  const freezeReason = pickPair(block, 'freeze_reason', isDe);
  const interfaceNote = pickPair(current, 'data_interface_note', isDe);
  const policy = pickPair(payload, 'promotion_policy', isDe);
  const events = (Array.isArray(payload?.events) ? payload.events : [])
    .slice()
    .sort((a, b) => String(b?.created_at || b?.at || '').localeCompare(String(a?.created_at || a?.at || '')));
  return (
    <div className="space-y-6">
      <AdminSection id="llm-monitor-arch" title={m.archTitle} subtitle={m.archSubtitle}
        state={m[`stateWord_${current.status || payload?.status}`] || current.status || payload?.status || ''} tone={frozen ? 'warning' : 'ok'}>
        <div className="space-y-3">
          <FactGrid items={[
            [m.codename, current.codename],
            [m.statusLabel, m[`stateWord_${current.status}`] || current.status],
            [m.interfaceStatus, m[`stateWord_${current.data_interface_status}`] || current.data_interface_status],
            [m.specSha, current.spec_sha256 ? String(current.spec_sha256).slice(0, 16) : ''],
            [m.promotedAt, current.promoted_at ? formatBerlin(current.promoted_at) : ''],
            [m.frozenLabel, frozen ? m.frozenYes : m.frozenNo],
          ]} />
          {interfaceNote ? <p className="text-sm text-text-secondary">{interfaceNote}</p> : null}
          {frozen ? (
            <div className="homepage-muted-panel p-3 space-y-1 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="admin-status-pill is-warning">{m.frozenLabel}</span>
                {block?.frozen_at ? <span className="text-xs text-text-secondary">{formatBerlin(block.frozen_at)}</span> : null}
                {block?.frozen_by_training_job ? <code className="font-mono text-xs">{block.frozen_by_training_job}</code> : null}
              </div>
              {freezeReason ? <p>{freezeReason}</p> : null}
            </div>
          ) : null}
        </div>
      </AdminSection>
      <ImplementationPanel block={block} payload={payload} autopilot={autopilot} corpus={corpus} m={m} isDe={isDe} lang={lang} />
      <AdminSection id="llm-monitor-vram" title={m.vramTitle} subtitle={m.vramSubtitle}>
        {Object.keys(budget).length
          ? <FactGrid items={vramFacts(budget, isDe, lang, m)} />
          : <p className="text-sm text-text-secondary">{m.vramNone}</p>}
      </AdminSection>
      <KernelSection kernel={kernel} corpus={corpus} tdvInfo={tdvInfo} latestRun={latestRun} m={m} lang={lang} plan={plan} />
      <AdminSection id="llm-monitor-evidence" title={m.evidenceTitle} count={evaluations.length}
        tone={evaluations.length ? 'ok' : ''}>
        {evaluations.length ? (
          <table className="admin-table">
            <thead>
              <tr><th>{m.benchmark}</th><th>{m.suite}</th><th>{m.score}</th><th>{m.evaluator}</th><th>{m.date}</th></tr>
            </thead>
            <tbody>
              {evaluations.map((row, i) => (
                <tr key={i}>
                  <td>{row.benchmark_id || row.metric || '—'}</td>
                  <td>{row.suite_version || '—'}</td>
                  <td className="font-mono text-xs">{row.score ?? '—'}</td>
                  <td>{row.evaluator || '—'}</td>
                  <td>{row.created_at ? formatBerlin(row.created_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-sm text-text-secondary">{pickPair(evidence, 'note', isDe) || m.evidenceNone}</p>}
      </AdminSection>
      <AdminSection id="llm-monitor-events" title={m.eventsTitle} count={events.length} collapsed>
        {events.length ? (
          <div className="space-y-2">
            {events.map((event, index) => (
              <div key={index} className="homepage-muted-panel p-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
                {event?.created_at || event?.at ? <span className="font-mono">{formatBerlin(event.created_at || event.at)}</span> : null}
                {Object.entries(event || {})
                  .filter(([key, value]) => !['created_at', 'at'].includes(key)
                    && !(key.endsWith('_en') && key.slice(0, -3) in event)
                    && value !== null && value !== undefined && typeof value !== 'object')
                  .map(([key, value]) => (
                    <span key={key}>
                      <span className="text-text-secondary">{factLabel(key)}: </span>
                      {event[`${key}_en`] !== undefined ? pickPair(event, key, isDe) : String(value)}
                    </span>
                  ))}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-text-secondary">{m.eventsNone}</p>}
      </AdminSection>
    </div>
  );
};

const LlmPaperPage = ({ section = 'paper' }) => {
  const { t, currentLang } = useTranslation();
  const isDe = currentLang === 'de';
  const p = t.admin.llmPaper || {};
  const m = t.admin.llmMonitor || {};
  const view = section === 'monitoring' ? 'monitoring' : 'paper';
  const [data, setData] = useState(null);
  const [autopilot, setAutopilot] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [overview, setOverview] = useState(null);
  const [corpus, setCorpus] = useState(null);
  const [kernel, setKernel] = useState(null);
  const [tdvInfo, setTdvInfo] = useState(null);
  const [strand, setStrand] = useState('');
  const [fetchedAt, setFetchedAt] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);

  // One fetch on opening plus a manual refresh; the document changes with design iterations, not by the
  // second. The autopilot snapshot (sample progress for the implementation panel) is feature-detected:
  // without it the panel says so instead of inventing progress.
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const payload = await requestJson(ARCHITECTURE_ENDPOINT);
      setData(payload);
      setStrand((chosen) => chosen || payload?.training_strand
        || Object.keys(payload?.strands || {})[0] || '');
      setFetchedAt(new Date());
      setError(false);
    } catch (fetchError) {
      setError(true);
    }
    // The lifecycle strip's lake-side facts (D-HP-ADMIN-073) are feature-detected one by
    // one: a missing door leaves its steps honestly open instead of failing the page.
    const [autopilotResult, readinessResult, overviewResult, corpusResult, kernelResult, tdvResult] =
      await Promise.allSettled([
        requestJson(AUTOPILOT_ENDPOINT), requestJson(READINESS_ENDPOINT), requestJson(OVERVIEW_ENDPOINT),
        requestJson(CORPUS_ENDPOINT), requestJson(KERNEL_ENDPOINT), requestJson(TDV_ENDPOINT)]);
    setAutopilot(autopilotResult.status === 'fulfilled' ? autopilotResult.value : null);
    setReadiness(readinessResult.status === 'fulfilled' ? readinessResult.value : null);
    setOverview(overviewResult.status === 'fulfilled' ? overviewResult.value : null);
    setCorpus(corpusResult.status === 'fulfilled' ? corpusResult.value : null);
    setKernel(kernelResult.status === 'fulfilled' ? kernelResult.value : null);
    // The latest sealed version's measured token mass (D-SAAS-146 §2) feeds the time plan.
    let sealed = null;
    if (tdvResult.status === 'fulfilled' && Array.isArray(tdvResult.value?.tdvs)) {
      const latest = tdvResult.value.tdvs.filter((row) => row.status === 'sealed').slice(-1)[0];
      if (latest?.tdv_id) {
        try {
          const detail = await requestJson(`${TDV_ENDPOINT}/${encodeURIComponent(latest.tdv_id)}`);
          const coverage = detail?.manifest?.coverage || {};
          sealed = { tdv_id: latest.tdv_id, token_total: Number(coverage.token_total || 0),
            tokens_by_stage: coverage.tokens_by_stage || null };
        } catch (detailError) {
          sealed = { tdv_id: latest.tdv_id, token_total: 0 };
        }
      }
    }
    setTdvInfo(sealed);
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // The strands this page shows (D-HP-ADMIN-071): the PAPER view
  // renders EVERY strand's document on the page, the any-to-any paper first exactly where it
  // always stood and the document strand's paper beneath it — nothing replaces the existing
  // paper, and the only switch anywhere is the TRAINING selection on the training page. The
  // monitoring view keeps a per-strand selector (operational state is read one strand at a
  // time). A payload without `strands` falls back to the single-strand rendering.
  const strands = data?.strands && typeof data.strands === 'object' ? data.strands : null;
  const strandNames = strands
    ? ['omni', ...Object.keys(strands).filter((name) => name !== 'omni')]
        .filter((name) => strands[name])
    : [];
  const activeStrand = strands ? (strands[strand] ? strand : strandNames[0]) : '';
  const block = strands ? strands[activeStrand] : data;

  // Printing this page (export button or Ctrl+P) scopes the print-isolation CSS via a body class
  // (D-HP-ADMIN-069): the paper prints complete and in flow, every other page prints normally.
  useEffect(() => {
    const mark = () => document.body.classList.add('llm-paper-printing');
    const unmark = () => document.body.classList.remove('llm-paper-printing');
    window.addEventListener('beforeprint', mark);
    window.addEventListener('afterprint', unmark);
    return () => {
      window.removeEventListener('beforeprint', mark);
      window.removeEventListener('afterprint', unmark);
      unmark();
    };
  }, []);

  const scrollToStrand = (name) => {
    const target = document.getElementById(`llm-paper-strand-${name}`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const chrome = (
    <div className="llm-paper-toolbar text-sm">
      {strandNames.length > 1 ? (
        <span className="flex items-center gap-1">
          {strandNames.map((name) => (
            <button key={name} type="button"
              className={`homepage-button-secondary text-xs${name === activeStrand && view === 'monitoring' ? ' is-active' : ''}`}
              onClick={() => (view === 'monitoring' ? setStrand(name) : scrollToStrand(name))}>
              {strands[name]?.current?.codename || strands[name]?.strand_title_en || name}
              {data?.training_strand === name ? ` · ${autopilot?.phase === 'training' ? p.strandTraining : p.strandSelected}` : ''}
            </button>
          ))}
        </span>
      ) : null}
      <span className="text-text-secondary">{view === 'monitoring' ? m.summary : p.summaryBody}</span>
      <button type="button" className="homepage-button-secondary text-xs" disabled={busy} onClick={load}>{p.refresh}</button>
      {fetchedAt ? <span className="text-xs text-text-secondary">{p.lastUpdated}: {fetchedAt.toLocaleTimeString(currentLang)}</span> : null}
      {error ? <span className="text-xs text-error">{p.unavailable}</span> : null}
    </div>
  );

  if (busy && !data) {
    return (<>{chrome}<div className="homepage-muted-panel p-4 text-sm text-text-secondary">{p.loading}</div></>);
  }
  if (view === 'monitoring') {
    if (!data) {
      return (<>{chrome}<div className="homepage-muted-panel p-4 text-sm text-text-secondary">{p.unavailable}</div></>);
    }
    const runs = Array.isArray(overview?.runs) ? overview.runs : [];
    const latestRun = runs.length ? (runs[0]?.metrics?.latest || runs[0]?.last_heartbeat || runs[0]) : null;
    return (<>{chrome}<LlmMonitorView block={block} payload={data} autopilot={autopilot} corpus={corpus}
      kernel={kernel} tdvInfo={tdvInfo} latestRun={latestRun} m={m} isDe={isDe} lang={currentLang}
      plan={((readiness?.stages || []).find((stage) => stage?.id === 'data_plan') || {}).plan || null} /></>);
  }
  // The paper view: EVERY strand's document SIDE BY SIDE (D-HP-ADMIN-071) —
  // the any-to-any paper left where it always stood, the document strand's paper next to
  // it on the right, both fully visible at once. Each document carries its own compact
  // status line and its own server-typeset PDF download.
  const paperBlocks = (strands ? strandNames.map((name) => [name, strands[name]]) : [['', data]])
    .filter(([, candidate]) => candidate?.paper);
  if (!paperBlocks.length) {
    return (
      <>
        {chrome}
        <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{error ? p.unavailable : p.noPaper}</div>
      </>
    );
  }
  return (
    <>
      {chrome}
      <div className="llm-paper-duo">
        {paperBlocks.map(([name, strandBlock]) => {
          const current = strandBlock?.current || {};
          return (
            <section key={name || 'paper'} id={`llm-paper-strand-${name}`}>
              <div className="llm-paper-toolbar text-sm">
                <span className="llm-paper-strong">{current.codename || strandBlock?.strand_title_en || name}</span>
                <span className="text-xs text-text-secondary">
                  {current.display_version || current.version || ''}
                  {current.data_interface_status ? ` · ${current.data_interface_status}` : ''}
                </span>
                {/* "training" only while a run trains; otherwise the strand is named as the selected one. */}
                {data?.training_strand === name
                  ? <span className={`admin-status-pill ${autopilot?.phase === 'training' ? 'is-ok' : 'is-neutral'}`}>
                    {autopilot?.phase === 'training' ? p.strandTraining : p.strandSelected}</span> : null}
                <a className="homepage-button-secondary text-xs"
                  href={`${ARCHITECTURE_ENDPOINT}/paper.pdf${name ? `?strand=${encodeURIComponent(name)}` : ''}`}
                  download>{p.exportPdf}</a>
              </div>
              <PaperDocument payload={strandBlock} p={p} />
              <StrandLifecycle name={name} block={strandBlock} payload={data}
                autopilot={autopilot} readiness={readiness} overview={overview} corpus={corpus}
                p={p} lang={currentLang} />
            </section>
          );
        })}
      </div>
    </>
  );
};

export default LlmPaperPage;
